import { getPushtypes, updatePushtypes } from 'flow/actions/extension-actions';
import { selectHasCapability } from 'flow/selectors/ExtensionSelectors';
import Loading from 'flow/components/ui-components/Loading';
import Tabs from 'flow/components/ui-components/Tabs';
import Toggle from 'flow/components/ui-components/Toggle';
import useAppContext from 'flow/hooks/useAppContext';
import useExtension from 'flow/hooks/useExtension';
import useProfiles from 'flow/hooks/useProfiles';
import { ExtensionKey, ProfileKey } from 'flow/types/EntityKeys';
import { NotificationSetting, PushType } from 'flow/types/Extension';
import { routes } from 'flow/types/routes';

import { TypedMap } from 'flow/types/TypedMap';
import { Device } from 'flow/types/Types';
import {
    getDefaultNotificationSetting,
    updateProfileNotificationType,
    updatePushTypeSetting,
} from 'flow/utils/notification-utils';
import { useEffect, useState } from 'react';
import { useAppSelector, useAppDispatch } from 'flow/hooks/useRedux';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import Page from '../Page';
import SettingRow from '../SettingRow';
import { generatePath } from 'flow/utils/router-utils';
import { Capabilities } from 'flow/utils/capability-utils';

interface Tab {
    key: Device;
    label: string;
}

const excludedPushTypes = ['incoming_queue_call'];

const ProfileNotifications = () => {
    const { i18next } = useAppContext();
    const dispatch = useAppDispatch();
    const history = useHistory();
    const { pathname, search } = useLocation();
    const { extensionKey, profileKey } = useParams<{
        extensionKey: ExtensionKey;
        profileKey: ProfileKey;
    }>();
    const hasCapabilitySms = useAppSelector((state) =>
        selectHasCapability(state, extensionKey, Capabilities.SMS),
    );

    const queryParams = new URLSearchParams(search);
    const type: Device = (queryParams.get('type') as Device) || 'desktop';

    const handleUpdateQuery = (newValue: Device) => {
        queryParams.set('type', newValue);

        history.replace({
            pathname,
            search: queryParams.toString(),
        });
    };

    const [device, setDevice] = useState<Device>(type);
    const [disabledAllToggles, setDisabledAllToggles] = useState(false);

    const { extension } = useExtension(extensionKey);
    const pushTypes = extension.get('pushTypes');
    const { profiles } = useProfiles(extensionKey);

    useEffect(() => {
        if (profiles && pushTypes && type) {
            handleUpdateQuery(type);
        }
    }, [type, profiles, pushTypes]);

    if (!pushTypes || !profiles) {
        return <Loading />;
    }

    const tabs: Tab[] = [
        {
            key: 'desktop',
            label: i18next.t('settings.notifications.device.desktop'),
        },
        {
            key: 'mobile',
            label: i18next.t('settings.notifications.device.mobile'),
        },
        {
            key: 'email',
            label: i18next.t('settings.notifications.device.email'),
        },
        { key: 'sms', label: i18next.t('settings.notifications.device.sms') },
    ];

    const filteredTabs = tabs.filter(
        (tab) => tab.key !== 'sms' || hasCapabilitySms,
    );

    const updateProfile = async (updatedMap: TypedMap<unknown>) => {
        setDisabledAllToggles(true);
        await dispatch(updatePushtypes(extensionKey, updatedMap));
        await dispatch(getPushtypes(extensionKey));
        setDisabledAllToggles(false);
    };

    const handleChange = async (
        pushType: TypedMap<PushType>,
        value: boolean,
    ) => {
        const updatedPushTypeSetting = updateProfileNotificationType(
            pushType,
            profileKey,
            device,
            value,
        );

        const updatedPushTypes = updatePushTypeSetting(
            pushTypes,
            updatedPushTypeSetting,
            pushType.get('name'),
        );

        updateProfile(updatedPushTypes);
    };

    return (
        <Page
            header="settings.notifications"
            onClickBack={() =>
                history.push(
                    generatePath(routes['settings.profiles.profileKey'], {
                        extensionKey,
                        profileKey,
                    }),
                )
            }
        >
            <p className="mt-4 mb-6">
                {i18next.t('settings.notifications_conf')}
            </p>

            <Tabs
                className="mb-3"
                tabs={filteredTabs}
                selectedKey={device}
                onClick={({ key }: Tab) => {
                    setDevice(key);
                    handleUpdateQuery(key);
                }}
            />

            {pushTypes
                .filter(
                    (pushType) =>
                        !excludedPushTypes.includes(pushType.get('name')),
                )
                .map((pushType) => {
                    const defaultSetting: TypedMap<NotificationSetting> =
                        getDefaultNotificationSetting(
                            pushType.get('notificationSettings'),
                        );

                    const toggled = (() => {
                        const setting = pushType
                            .get('notificationSettings')
                            .find(
                                (setting) =>
                                    setting.getIn(['profile', 'key']) ===
                                    profileKey,
                            );

                        if (setting?.has(device)) {
                            return setting.get(device);
                        }

                        return defaultSetting?.get(device) || false;
                    })();

                    return (
                        <SettingRow
                            key={pushType.get('name')}
                            cta={
                                <Toggle
                                    value={toggled}
                                    onChange={(value) =>
                                        handleChange(pushType, value)
                                    }
                                    disabled={disabledAllToggles}
                                    text={i18next.t(
                                        `settings.${pushType.get('name')}`,
                                    )}
                                />
                            }
                        >
                            {i18next.t(`settings.${pushType.get('name')}`)}
                        </SettingRow>
                    );
                })}
        </Page>
    );
};

export default ProfileNotifications;
